import type { ComponentChildren } from "preact"
import { useState } from "preact/hooks"
import type { QuestionImage } from "../types"
import { BoardShell } from "./BoardShell"
import { IconUndo } from "./Icons"

interface SpellBoardProps {
	// Status line content (question counter, score) shown above the prompt.
	header: ComponentChildren
	prompt: string
	image: QuestionImage | null
	// The keypad, in display order. Positional, not a set of letters:
	// "twenty" ships two "t" tiles and tapping one uses up *that* tile, so
	// everything here works in tile indices rather than in letters.
	tiles: string[]
	// True while the answer is in flight, and for the whole reveal.
	disabled: boolean
	onAnswer: (text: string) => void
	// Set once the answer has been graded: what she built, whether it was
	// right, and the spelling it should have been. Both words are shown —
	// seeing her attempt beside the real word is the teaching moment this
	// topic exists for, so the reveal never shows only the verdict.
	reveal: { text: string; isCorrect: boolean; correct: string } | null
	// Optional content under the answer area (errors).
	footer?: ComponentChildren
}

// SpellBoard is the play surface for a spell question: the numeral is the
// prompt and the answer is built letter by letter from a keypad of tiles.
// Everything above the answer area is BoardShell, shared with QuestionBoard.
//
// The caller must give it a `key` that changes with the question — the placed
// tiles are local state, and a new question starts from an empty word.
export function SpellBoard({
	header,
	prompt,
	image,
	tiles,
	disabled,
	onAnswer,
	reveal,
	footer,
}: SpellBoardProps) {
	// Indices into `tiles`, in the order they were tapped. Indices, not
	// letters: two "t" tiles are two different tiles, and only the one that
	// was tapped is used up.
	const [placed, setPlaced] = useState<number[]>([])
	const used = new Set(placed)
	const built = placed.map((i) => tiles[i]).join("")

	// While revealing, the word on top is the graded one from the server
	// rather than the local build — they are the same string, but the server's
	// is what was actually marked.
	const shownWord = reveal ? reveal.text : built
	const wordTone = reveal
		? reveal.isCorrect
			? "text-success"
			: "text-danger"
		: ""

	return (
		<BoardShell header={header} prompt={prompt} image={image} footer={footer}>
			<div class="board-spell">
				{/* The word so far. It keeps its height when empty so the tiles
				    below do not jump down on the first tap. */}
				<div class="board-word-row">
					<div class={`board-word ${wordTone}`}>
						{shownWord ? (
							shownWord
						) : (
							<span class="opacity-50">&middot;&middot;&middot;</span>
						)}
					</div>
					<button
						type="button"
						class="sm"
						disabled={disabled || placed.length === 0}
						onClick={() => setPlaced(placed.slice(0, -1))}
					>
						<IconUndo /> Undo
					</button>
				</div>

				{/* The keypad. A used tile stays where it was, dimmed — reflowing
				    the letters on every tap would move the one she is reaching for.
				    Plain onClick rather than the choice board's pointerdown: placing
				    a letter is not an answer, and Undo takes it back, so there is
				    nothing to gain by racing the finger lift. */}
				<div class="board-tiles">
					{tiles.map((letter, i) => (
						<button
							type="button"
							key={i}
							class={used.has(i) ? "opacity-50" : "outline"}
							disabled={disabled || used.has(i)}
							onClick={() => setPlaced([...placed, i])}
						>
							{letter}
						</button>
					))}
				</div>

				{reveal ? (
					// The answer, spelled right, whether or not she got it. When she
					// did, this confirms it; when she did not, it is the only place
					// the real spelling appears.
					<p class="text-center">
						<span class="muted small block">
							{reveal.isCorrect ? "That's it" : "It's spelled"}
						</span>
						<span class="fw-semibold">{reveal.correct}</span>
					</p>
				) : (
					<button
						type="button"
						class="primary lg"
						disabled={disabled || placed.length === 0}
						onClick={() => onAnswer(built)}
					>
						Done
					</button>
				)}
			</div>
		</BoardShell>
	)
}
